import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddTaskIcon from '@mui/icons-material/AddTask';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatDate } from '../i18n/format';
import { auditsApi } from '../services/audits';
import AxisScheduleControl from '../components/brand/AxisScheduleControl';
import AuditCapConfig from '../components/brand/AuditCapConfig';
import CostEstimate from '../components/billing/CostEstimate';
import HowItWorksCard from '../components/HowItWorksCard';
import { tasksApi } from '../services/tasks';
import type { Audit, EarnedMediaAuditSummary, AgentStatus, AuthorityGap, EngineInfluenceBlock } from '@traaker/shared';
import { classifyRecommendation, fromStringList } from '@traaker/shared';
import { RecommendationList } from '../components/recommendations/RecommendationList';
import { EarnedMediaV2Tabs } from '../components/earned-media/EarnedMediaV2Tabs';

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

// i18n keys — resolved with `t()` at render (labels live in module scope).
const STATUS_LABEL_KEYS: Record<string, string> = {
  pending: 'earnedMedia.status.pending',
  running: 'earnedMedia.status.running',
  completed: 'earnedMedia.status.completed',
  failed: 'earnedMedia.status.failed',
};

const AGENT_STATUS_COLORS: Record<AgentStatus, string> = {
  pending: '#A0A4AE',
  running: '#F59E0B',
  completed: '#10B981',
  failed: '#EF4444',
};

const SENTIMENT_COLORS: Record<string, string> = {
  positive: '#00DDB3',
  neutral: '#8B9DC3',
  negative: '#FF4444',
};

const SOURCE_TYPE_LABEL_KEYS: Record<string, string> = {
  press: 'earnedMedia.sourceType.press',
  academic: 'earnedMedia.sourceType.academic',
  industry: 'earnedMedia.sourceType.industry',
  review: 'earnedMedia.sourceType.review',
  wikipedia: 'earnedMedia.sourceType.wikipedia',
  forum: 'earnedMedia.sourceType.forum',
};

const MEDIA_TYPE_LABELS: Record<string, string> = {
  earned: 'Earned',
  owned: 'Owned',
  social: 'Social',
};

const MEDIA_TYPE_COLORS: Record<string, string> = {
  earned: '#00DDB3',
  owned: '#8B9DC3',
  social: '#FF6B35',
};

/**
 * V2 discriminator — true when the summary was produced by the
 * orchestrator (Phase E2). Any one of these fields signals v2; absence of
 * all of them = legacy v1 audit, which renders only the v1 sections below.
 */
function _isV2Summary(s: EarnedMediaAuditSummary): boolean {
  return Boolean(
    s.recommendations_categorized ||
      s.engine_influence ||
      (s.missing_authorities && s.missing_authorities.length > 0) ||
      (s.conversations && s.conversations.length > 0),
  );
}

function ScoreBadge({ score, suffix = '/100' }: { score: number; suffix?: string }) {
  const color = score >= 70 ? '#00DDB3' : score >= 40 ? '#FF6B35' : '#FF4444';
  return (
    <Typography fontWeight={700} sx={{ color }}>
      {score}{suffix}
    </Typography>
  );
}

export default function EarnedMedia() {
  const { t } = useTranslation();
  const { activeBrand, refreshBrands } = useBrand();
  const { tenant } = useAuth();
  const navigate = useNavigate();
  // Earned Media runs on demand on every tier, paid in credits (pricing v2).
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [creditsShort, setCreditsShort] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);

  const loadAudits = async () => {
    try {
      const data = await auditsApi.list('earned_media', activeBrand?.brandId);
      setAudits(data);
    } catch {
      setError(t('earnedMedia.error.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setSelectedAudit(null);
    loadAudits();
  }, [activeBrand?.brandId]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    setCreditsShort(false);
    try {
      await auditsApi.create('earned_media', {}, activeBrand?.brandId);
      setDialogOpen(false);
      await loadAudits();
    } catch (err: any) {
      if (err?.body?.code === 'INSUFFICIENT_CREDITS') {
        setDialogOpen(false);
        setCreditsShort(true);
        setError(t('earnedMedia.error.insufficientCredits'));
      } else {
        setError(err.message || t('earnedMedia.error.create'));
      }
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Detail view
  if (selectedAudit) {
    return (
      <EarnedMediaDetail
        audit={selectedAudit}
        onBack={() => setSelectedAudit(null)}
        onRefresh={async () => {
          const updated = await auditsApi.getById(selectedAudit.auditId);
          if (updated) setSelectedAudit(updated);
        }}
      />
    );
  }

  // List view
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700} data-tour="page-intro">
          {t('earnedMedia.title')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeBrand?.brandId && (
            <Box component="span" data-tour="schedule-control" sx={{ display: 'inline-flex' }}>
              <AxisScheduleControl
                brandId={activeBrand.brandId}
                tier={tenant?.tier || ''}
                axis="earned_media"
                cadenceOverride={activeBrand.auditConfig?.cadenceOverride}
                onSaved={refreshBrands}
              />
            </Box>
          )}
          {activeBrand?.brandId && (
            <Box component="span" data-tour="cap-control" sx={{ display: 'inline-flex' }}>
              <AuditCapConfig
                brandId={activeBrand.brandId}
                kind="earned_media"
                value={activeBrand.auditConfig?.maxFetchPerEarnedMedia}
                onSaved={refreshBrands}
              />
            </Box>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            {t('earnedMedia.newAudit')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => { setError(''); setCreditsShort(false); }}
          action={creditsShort ? (
            <Button color="inherit" size="small" onClick={() => navigate('/credits/ledger')}>
              {t('billing.viewCreditHistory')}
            </Button>
          ) : undefined}
        >
          {error}
        </Alert>
      )}

      <HowItWorksCard
        title={t('earnedMedia.howItWorks.title')}
        steps={[
          {
            title: t('earnedMedia.howItWorks.discover.title'),
            description: t('earnedMedia.howItWorks.discover.description'),
            image: '/assets/how-it-works/earned-media-discover.webp',
          },
          {
            title: t('earnedMedia.howItWorks.select.title'),
            description: t('earnedMedia.howItWorks.select.description'),
            image: '/assets/how-it-works/earned-media-select.webp',
          },
          {
            title: t('earnedMedia.howItWorks.score.title'),
            description: t('earnedMedia.howItWorks.score.description'),
            image: '/assets/how-it-works/earned-media-score.webp',
          },
        ]}
      />

      {audits.length === 0 ? (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('earnedMedia.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('earnedMedia.empty.body', { brand: activeBrand?.name || t('earnedMedia.yourBrand') })}
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              {t('earnedMedia.launchAudit')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('earnedMedia.table.date')}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {t('earnedMedia.table.pagesAnalyzed')}
                  </Box>
                </TableCell>
                <TableCell>{t('earnedMedia.table.status')}</TableCell>
                <TableCell>{t('earnedMedia.table.agent')}</TableCell>
                <TableCell>{t('earnedMedia.table.score')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audits.map((audit) => {
                const agentStatus = audit.agentStatuses?.earned_media || 'pending';
                return (
                  <TableRow
                    key={audit.auditId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedAudit(audit)}
                  >
                    <TableCell>
                      {formatDate(audit.createdAt, {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>{audit.earnedMediaSummary?.fetch_count ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABEL_KEYS[audit.status] ? t(STATUS_LABEL_KEYS[audit.status]) : audit.status}
                        color={STATUS_COLORS[audit.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`Earned Media: ${STATUS_LABEL_KEYS[agentStatus] ? t(STATUS_LABEL_KEYS[agentStatus]) : agentStatus}`}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: AGENT_STATUS_COLORS[agentStatus] || '#555',
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>
                        {audit.earnedMediaSummary?.overall_earned_media_score ?? '—'}
                        {audit.earnedMediaSummary?.overall_earned_media_score !== undefined && '/100'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('earnedMedia.dialog.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body1">
              {t('earnedMedia.dialog.analyzePrefix')} <strong>{activeBrand?.name || '—'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('earnedMedia.dialog.description')}
            </Typography>
            {!activeBrand?.strategy && (
              <Alert severity="warning">
                {t('earnedMedia.dialog.noStrategyWarning')}
              </Alert>
            )}
            <CostEstimate
              auditType="earned_media"
              maxFetchOverride={activeBrand?.auditConfig?.maxFetchPerEarnedMedia}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>{t('earnedMedia.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : t('earnedMedia.dialog.launch')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Detail view ──

function EarnedMediaDetail({
  audit: initialAudit,
  onBack,
  onRefresh,
}: {
  audit: Audit;
  onBack: () => void;
  onRefresh: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { activeBrand } = useBrand();
  const toast = useToast();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(initialAudit);
  const [summary, setSummary] = useState<EarnedMediaAuditSummary | null>(
    initialAudit.earnedMediaSummary || null,
  );
  const [sources, setSources] = useState<any[]>([]);
  const [inaccuracies, setInaccuracies] = useState<any[]>([]);
  const [engineInfluence, setEngineInfluence] = useState<EngineInfluenceBlock | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [auditData, summaryData, resultsData, influenceData] = await Promise.all([
        auditsApi.getById(audit.auditId),
        auditsApi.getSummary(audit.auditId).catch(() => null),
        auditsApi.getResults(audit.auditId, 'earned_media').catch(() => []),
        // Live, BP-owned engine-influence record — independent of this EM run.
        activeBrand?.brandId
          ? auditsApi.getEngineInfluence(activeBrand.brandId).catch(() => null)
          : Promise.resolve(null),
      ]);
      setEngineInfluence(influenceData?.block || null);
      if (auditData) {
        setAudit(auditData);
        setSummary(auditData.earnedMediaSummary || (summaryData as EarnedMediaAuditSummary | null) || null);
      }
      // Separate sources from inaccuracies based on SK prefix
      const srcList: any[] = [];
      const inaccList: any[] = [];
      for (const r of resultsData) {
        if (r.SK?.includes('INACCURACY')) {
          inaccList.push(r);
        } else {
          srcList.push(r);
        }
      }
      setSources(srcList);
      setInaccuracies(inaccList);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [audit.auditId, activeBrand?.brandId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await onRefresh();
    setRefreshing(false);
  };

  const agentStatus = audit.agentStatuses?.earned_media || 'pending';
  const isV2 = !!summary && _isV2Summary(summary);

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        {t('earnedMedia.back')}
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          {t('earnedMedia.detailTitle', { date: formatDate(audit.createdAt) })}
        </Typography>
        <Chip
          label={STATUS_LABEL_KEYS[audit.status] ? t(STATUS_LABEL_KEYS[audit.status]) : audit.status}
          color={STATUS_COLORS[audit.status] || 'default'}
        />
        <Chip
          label={t('earnedMedia.agentChip', { status: STATUS_LABEL_KEYS[agentStatus] ? t(STATUS_LABEL_KEYS[agentStatus]) : agentStatus })}
          size="small"
          sx={{
            bgcolor: `${AGENT_STATUS_COLORS[agentStatus]}20`,
            color: AGENT_STATUS_COLORS[agentStatus],
            fontWeight: 600,
          }}
        />
        <Tooltip title={t('earnedMedia.refresh')}>
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {audit.status === 'running' && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}

      {audit.status === 'pending' && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              {t('earnedMedia.pendingProcessing')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Summary cards — global header. Always rendered first when present. */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard title={t('earnedMedia.summary.overallScore')} value={`${summary.overall_earned_media_score}`} suffix="/100" color="#7C3AED" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard title="Wikipedia" value={`${summary.wikipedia_quality_score}`} suffix="/100" color="#8B5CF6" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard title="E-E-A-T" value={`${summary.eeat_score}`} suffix="/100" color="#00DDB3" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard title={t('earnedMedia.summary.precision')} value={`${summary.accuracy_score}`} suffix="/100" color="#FF6B35" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard title={t('earnedMedia.summary.sources')} value={`${summary.authority_mention_count}`} suffix="" color="#E91E63" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard title={t('earnedMedia.summary.inaccuracies')} value={`${summary.inaccuracies_count}`} suffix="" color="#FF4444" />
          </Grid>
        </Grid>
      )}

      {/* V2 tabs (orchestrator-produced summaries only). Rendered AFTER the
          score strip so the global header is always at the top of the page.
          v1 sections below are suppressed when v2 is present (their content
          lives in the tabs) to avoid duplication. */}
      {summary && _isV2Summary(summary) && (
        <EarnedMediaV2Tabs
          summary={summary}
          sources={sources}
          inaccuracies={inaccuracies}
          engineInfluence={engineInfluence}
          onCreateTask={async (title, severity) => {
            try {
              const { category } = classifyRecommendation({ text: title, auditType: 'earned_media' });
              await tasksApi.create({
                title: title.slice(0, 120),
                description: '',
                category: category === 'content' ? 'earned_media' : category,
                sourceType: 'audit_earned_media',
                priority: severity === 'high' ? 'high' : severity === 'low' ? 'low' : 'medium',
                auditId: audit.auditId,
                sourceAuditType: 'earned_media',
                originLabel: 'Médias',
                ...(activeBrand?.brandId ? { brandId: activeBrand.brandId } : {}),
              } as any);
              toast.show(t('earnedMedia.toast.addedToTasks'), { action: { label: t('earnedMedia.toast.view'), onClick: () => navigate('/tasks') } });
            } catch {
              toast.show(t('earnedMedia.toast.actionFailed'), { severity: 'error' });
            }
          }}
        />
      )}

      {/* Coverage gaps — v1-only; v2 surfaces this in the "Médias à conquérir" tab. */}
      {!isV2 && (summary?.coverage_gap_details?.length || summary?.coverage_gaps?.length) ? (
        <Card sx={{ mb: 3, bgcolor: 'rgba(255, 107, 53, 0.04)' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ color: '#FF6B35' }} />
              {t('earnedMedia.coverageGap.title', { count: summary.coverage_gap_details?.length || summary.coverage_gaps?.length })}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {t('earnedMedia.coverageGap.subtitle')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {((summary.coverage_gap_details
                ?? summary.coverage_gaps?.map((d) => ({ domain: d } as AuthorityGap))
                ?? []) as AuthorityGap[]
              ).map((gap) => (
                <Box key={gap.domain} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Chip label={gap.domain} size="small" variant="outlined" sx={{ mt: 0.25 }} />
                  {gap.category && (
                    <Chip
                      label={String(gap.category).replace(/_/g, ' ')}
                      size="small"
                      sx={{ mt: 0.25, bgcolor: 'rgba(124, 58, 237, 0.10)', color: '#7C3AED' }}
                    />
                  )}
                  {gap.rationale && (
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                      {gap.rationale}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      ) : null}

      {/* Recommendations — v1-only flat list; v2 splits these into per-tab sections. */}
      {!isV2 && summary?.recommendations && summary.recommendations.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <RecommendationList
            recommendations={fromStringList(summary.recommendations, {
              auditType: 'earned_media',
              originLabel: 'Médias',
            })}
            auditId={audit.auditId}
            brandId={activeBrand?.brandId}
          />
        </Box>
      )}

      {/* Sources table — v1-only. v2 renders sources inside the "Médias acquis" tab
          with inaccuracy join + expandable details, so this would duplicate.
          The whole block is gated on !isV2 — including the empty-state "Aucune
          source trouvée" branch — so v2 audits don't render a misleading empty
          card on every tab. */}
      {isV2 ? null : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : sources.length > 0 ? (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ pb: 0 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              {t('earnedMedia.sourcesFound', { count: sources.length })}
            </Typography>
          </CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('earnedMedia.sourcesTable.source')}</TableCell>
                  <TableCell>{t('earnedMedia.sourcesTable.type')}</TableCell>
                  <TableCell>{t('earnedMedia.sourcesTable.media')}</TableCell>
                  <TableCell>{t('earnedMedia.sourcesTable.sentiment')}</TableCell>
                  <TableCell>{t('earnedMedia.sourcesTable.relevance')}</TableCell>
                  <TableCell>{t('earnedMedia.sourcesTable.accurate')}</TableCell>
                  <TableCell width={40} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sources.map((s: any, i: number) => (
                  <TableRow key={s.SK || i}>
                    <TableCell sx={{ maxWidth: 250 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {s.sourceName}
                      </Typography>
                      {s.excerpt && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                          {s.excerpt}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={SOURCE_TYPE_LABEL_KEYS[s.sourceType] ? t(SOURCE_TYPE_LABEL_KEYS[s.sourceType]) : s.sourceType}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={MEDIA_TYPE_LABELS[s.mediaType] || s.mediaType}
                        size="small"
                        sx={{
                          bgcolor: `${MEDIA_TYPE_COLORS[s.mediaType] || '#888'}20`,
                          color: MEDIA_TYPE_COLORS[s.mediaType] || '#888',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={s.sentiment}
                        size="small"
                        sx={{
                          bgcolor: `${SENTIMENT_COLORS[s.sentiment] || '#888'}20`,
                          color: SENTIMENT_COLORS[s.sentiment] || '#888',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <ScoreBadge score={s.relevanceScore ?? 0} suffix="" />
                    </TableCell>
                    <TableCell>
                      {s.brandAccurate ? (
                        <Chip label={t('earnedMedia.yes')} size="small" color="success" variant="outlined" />
                      ) : (
                        <Chip label={t('earnedMedia.no')} size="small" color="error" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {s.sourceUrl && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(s.sourceUrl, '_blank');
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      ) : audit.status === 'completed' ? (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Typography color="text.secondary">{t('earnedMedia.noSources')}</Typography>
          </CardContent>
        </Card>
      ) : null}

      {/* Inaccuracies — v1-only. v2 renders these inline inside acquired-domain rows
          + as full cards in the "Précision" tab. */}
      {!isV2 && inaccuracies.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ color: '#FF6B35' }} />
              {t('earnedMedia.inaccuraciesDetected', { count: inaccuracies.length })}
            </Typography>
            {inaccuracies.map((item: any, i: number) => (
              <InaccuracyCard
                key={item.SK || i}
                item={item}
                auditId={audit.auditId}
                brandId={activeBrand?.brandId}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

function InaccuracyCard({ item, auditId, brandId }: { item: any; auditId: string; brandId?: string }) {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const tasksEnabled = !!tenant?.settings?.featureFlags?.tasksEnabled;

  const handleAdd = async () => {
    if (added || adding) return;
    setAdding(true);
    try {
      await tasksApi.create({
        title: t('earnedMedia.inaccuracy.taskTitle', { source: item.sourceName }).slice(0, 120),
        description: t('earnedMedia.inaccuracy.taskDescription', { claim: item.claim, reality: item.reality }),
        category: 'earned_media',
        sourceType: 'audit_earned_media',
        auditId,
        brandId,
        sourcePageUrl: item.sourceUrl,
      });
      setAdded(true);
    } catch { /* ignore */ } finally {
      setAdding(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 1.5, bgcolor: 'rgba(255, 107, 53, 0.04)' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {item.sourceName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={item.severity === 'critical' ? t('earnedMedia.severity.critical') : t('earnedMedia.severity.minor')}
              size="small"
              color={item.severity === 'critical' ? 'error' : 'warning'}
            />
            {tasksEnabled && (
              <Tooltip title={added ? t('earnedMedia.addedToTasks') : t('earnedMedia.addToTasks')}>
                <IconButton size="small" onClick={handleAdd} disabled={adding}>
                  {adding ? (
                    <CircularProgress size={14} />
                  ) : added ? (
                    <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#00DDB3' }} />
                  ) : (
                    <AddTaskIcon fontSize="small" sx={{ color: '#7C3AED' }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          <strong>{t('earnedMedia.inaccuracy.claimLabel')}</strong> {item.claim}
        </Typography>
        <Typography variant="body2" sx={{ color: '#00DDB3' }}>
          <strong>{t('earnedMedia.inaccuracy.realityLabel')}</strong> {item.reality}
        </Typography>
      </CardContent>
    </Card>
  );
}

function SummaryCard({ title, value, suffix, color }: {
  title: string;
  value: string;
  suffix: string;
  color: string;
}) {
  return (
    <Card>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography variant="h5" fontWeight={700} sx={{ color }}>
          {value}{suffix}
        </Typography>
      </CardContent>
    </Card>
  );
}
